/**
 * Cart Drawer Essentials Component
 * 
 * This component handles the display and functionality of recommended products
 * in the cart drawer. It integrates with the Rebuy API to fetch personalized
 * product recommendations based on items in the cart.
 * 
 * Key Features:
 * - Fetches recommendations from Rebuy API
 * - Displays up to 2 in-stock products
 * - Handles add-to-cart functionality with proper cart updates
 * - Includes smooth animations for product updates
 * - Auto-refreshes when cart contents change
 * 
 * Dependencies:
 * - Must be loaded before cart-drawer.js
 * - Requires Rebuy API configuration (window.rebuyConfig.apiKey)
 * - Uses global PubSub system for cart updates
 * 
 * @extends HTMLElement
 */
class CartDrawerEssentials extends HTMLElement {
  constructor() {
    super();
    this.rebuyApiKey = window.rebuyConfig?.apiKey;
    this.apiEndpoint = 'https://rebuyengine.com/api/v1/products/recommended';
  }

  connectedCallback() {
    this.fetchRecommendedProducts();
  }

  getCartVariantIds() {
    const cartItems = document.querySelectorAll('[data-quantity-variant-id]');
    const variantIds = Array.from(cartItems).map(item => item.dataset.quantityVariantId);
    return variantIds.filter(id => id);
  }

  async fetchRecommendedProducts() {
    const variantIds = this.getCartVariantIds();

    if (variantIds.length === 0) {
      this.renderEmptyState();
      return;
    }

    if (!this.rebuyApiKey) {
      console.log('Rebuy not configured!');
      this.hideSection();
      return;
    }

    const params = new URLSearchParams({
      key: this.rebuyApiKey,
      shopify_variant_ids: variantIds.join(','),
      limit: '10'
    });

    try {
      this.showSection();

      const response = await fetch(`${this.apiEndpoint}?${params}`);

      if (!response.ok) {
        console.warn(`Rebuy API responded with status: ${response.status}`);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      this.renderProducts(data);
    } catch (error) {
      console.error('Error fetching recommended products:', error);
      this.hideSection();
    }
  }

  renderProducts(data) {
    let products = [];
    if (data.data && Array.isArray(data.data)) {
      products = data.data;
    } else if (data.products && Array.isArray(data.products)) {
      products = data.products;
    } else if (Array.isArray(data)) {
      products = data;
    }

    if (products.length === 0) {
      this.hideSection();
      return;
    }

    // Filter out products that cannot be added to the cart and limit to 2 products
    const inStockProducts = products.filter(product => this.hasInventory(product)).slice(0, 2);

    if (inStockProducts.length === 0) {
      this.hideSection();
      return;
    }

    this.showSection();

    if (this.areProductsSame(inStockProducts)) {
      return;
    }

    const fragment = document.createDocumentFragment();
    inStockProducts.forEach(product => {
      const productElement = this.createProductElement(product);
      fragment.appendChild(productElement);
    });

    if (this.children.length > 0) {
      // Add fade-out class to existing items
      Array.from(this.children).forEach(child => {
        child.style.opacity = '0';
        child.style.transform = 'translateY(-5px)';
        child.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      });

      // After fade-out completes, replace with new products
      setTimeout(() => {
        this.innerHTML = '';

        // Add new products with fade-in animation
        Array.from(fragment.children).forEach((child, index) => {
          child.style.opacity = '0';
          child.style.transform = 'translateY(5px)';
          child.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
          this.appendChild(child);

          // Stagger the fade-in slightly for each item
          setTimeout(() => {
            child.style.opacity = '1';
            child.style.transform = 'translateY(0)';
          }, index * 100);
        });
      }, 300);
    } else {
      // No existing products, just add new ones with fade-in
      Array.from(fragment.children).forEach((child, index) => {
        child.style.opacity = '0';
        child.style.transform = 'translateY(5px)';
        child.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        this.appendChild(child);
        
        setTimeout(() => {
          child.style.opacity = '1';
          child.style.transform = 'translateY(0)';
        }, index * 100);
      });
    }
  }

  areProductsSame(newProducts) {
    const currentItems = this.querySelectorAll('.cart-drawer-essentials-item');

    if (currentItems.length !== newProducts.length) {
      return false;
    }

    for (let i = 0; i < newProducts.length; i++) {
      const newProduct = newProducts[i];
      const currentItem = currentItems[i];
      
      if (!currentItem) return false;

      const currentButton = currentItem.querySelector('button[data-variant-id]');
      if (!currentButton) return false;

      const currentVariantId = currentButton.dataset.variantId;

      const newVariantId = newProduct.variants?.[0]?.id || 
                          newProduct.variant_id || 
                          newProduct.id || 
                          '';

      if (currentVariantId !== newVariantId.toString()) {
        return false;
      }
    }

    return true;
  }

  hasInventory(product) {
    if (product.variants && product.variants.length > 0) {
      const variant = product.variants[0];
      return variant.inventory_management === null
    }

    return true;
  }

  createProductElement(product) {
    const productDiv = document.createElement('div');
    productDiv.className = 'cart-drawer-essentials-item';

    const image = product.image?.src ||
                 product.images?.[0]?.src ||
                 '';
    const imageAlt = product.image?.alt || product.images?.[0]?.alt || '';

    const title = product.title || product.name || 'Product';

    let price = '';
    if (product.price) {
      if (typeof product.price === 'number') {
        const priceValue = product.price > 100 ? product.price / 100 : product.price;
        price = this.formatPrice(priceValue);
      } else if (product.price.amount) {
        price = this.formatPrice(product.price.amount);
      }
    } else if (product.variants && product.variants[0]?.price) {
      price = this.formatPrice(product.variants[0].price);
    }

    const variantId = product.variants?.[0]?.id || 
                     product.variant_id || 
                     product.id || 
                     '';

    productDiv.innerHTML = `
      <div class="cart-drawer-essentials-item-image-wrapper">
        <img src="${image}" alt="${imageAlt}" loading="lazy">
      </div>
      <div class="cart-drawer-essentials-item-details">
        <h2>${title}</h2>
        <span>${price}</span>
        <button type="button" data-variant-id="${variantId}" onclick="this.closest('cart-drawer-essentials').addToCart('${variantId}')">Add</button>
      </div>
    `;
    
    return productDiv;
  }

  formatPrice(priceValue) {
    let price = typeof priceValue === 'string' ? parseFloat(priceValue) : priceValue;

    if (price > 100) {
      price = price / 100;
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  }

  renderEmptyState() {
    this.hideSection();
  }

  hideSection() {
    this.style.display = 'none';

    const headers = document.querySelectorAll('.cart-essentials-header');
    headers.forEach(header => {
      header.style.display = 'none';
    });
  }

  showSection() {
    this.style.display = '';

    const headers = document.querySelectorAll('.cart-essentials-header');
    headers.forEach(header => {
      header.style.display = '';
    });
  }

  async addToCart(variantId) {
    if (!variantId) return;

    const button = this.querySelector(`[data-variant-id="${variantId}"] button`) || event.target;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    const formData = {
      items: [{
        id: variantId,
        quantity: 1
      }]
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const cartResponse = await fetch('/cart.js');
        const fullCartData = await cartResponse.json();

        window.cart = fullCartData;

        publish(PUB_SUB_EVENTS.cartUpdate, { 
          source: 'cart-drawer-essentials', 
          cartData: fullCartData,
          variantId: variantId 
        });

        // Also trigger the custom cart event for backward compatibility
        const cartUpdateEvent = new CustomEvent('cart:updated', { detail: { cartData: fullCartData } });
        document.dispatchEvent(cartUpdateEvent);

        button.textContent = 'Added!';
        setTimeout(() => {
          button.textContent = originalText;
          button.disabled = false;
        }, 1500);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    }
  }
}

customElements.define('cart-drawer-essentials', CartDrawerEssentials);
